import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';


interface ModelOption {
  id: string;
  name: string;
  context: string;
  service: string;
}

interface ModelDefaults {
  default_assistant: string;
  default_model: string;

  plan: string;
  implement: string;
  push: string;
  complic: string;

  plan_assistant: string;
  implement_assistant: string;
  review_assistant: string;
  push_assistant: string;
  complic_assistant: string;
}

const REAL_ASSISTANTS = [
  { value: 'cursor', label: '✨ Cursor', description: 'Cursor local agent — high-quality multi-file edits' },
  { value: 'claude_cli', label: '🎯 Claude CLI', description: 'Official Anthropic tool - FREE, understands codebase' },
  { value: 'claude_code', label: '💬 Claude API', description: 'Free & fast direct API (no repo context)' },
  { value: 'aider', label: '⚡ Aider', description: 'Slow but full repo context analysis' },
  { value: 'claude_assistant', label: '🔧 Claude Assistant', description: 'Custom assistant with file discovery' },
];

const ACTION_ASSISTANTS = [
  { value: 'default', label: '📌 Default', description: 'Use assistant & model from Default section' },
  ...REAL_ASSISTANTS,
];

const ModelDefaultsSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableModels, setAvailableModels] = useState<ModelOption[]>([]);
  const [defaults, setDefaults] = useState<ModelDefaults>({
    default_assistant: 'claude_cli',
    default_model: '',
    plan: '',
    implement: '',
    push: '',
    complic: '',
    plan_assistant: 'default',
    implement_assistant: 'default',
    review_assistant: 'default',
    push_assistant: 'default',
    complic_assistant: 'default',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const [modelsResponse, defaultsResponse] = await Promise.all([
        axios.get(`${API_URL}/api/models/available`),
        axios.get(`${API_URL}/api/user/model-defaults`)
      ]);

      setAvailableModels(modelsResponse.data);

      const data = defaultsResponse.data;
      // Migrate legacy "auto" → "default" on load
      const migrateAssistant = (value: string | undefined) =>
        !value || value === 'auto' ? 'default' : value;

      setDefaults({
        default_assistant: data.default_assistant || 'claude_cli',
        default_model: data.default_model || '',
        plan: data.plan || '',
        implement: data.implement || '',
        push: data.push || '',
        complic: data.complic || '',
        plan_assistant: migrateAssistant(data.plan_assistant),
        implement_assistant: migrateAssistant(data.implement_assistant),
        review_assistant: migrateAssistant(data.review_assistant),
        push_assistant: migrateAssistant(data.push_assistant),
        complic_assistant: migrateAssistant(data.complic_assistant),
      });
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (newDefaults?: ModelDefaults) => {
    try {
      setSaving(true);
      const dataToSave = newDefaults || defaults;
      await axios.put(`${API_URL}/api/user/model-defaults`, dataToSave);
    } catch (err: any) {
      console.error('Failed to save defaults:', err);
      alert(err.response?.data?.detail || 'Failed to save defaults');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = async (field: keyof ModelDefaults, value: string) => {
    const newDefaults = { ...defaults, [field]: value };

    // When switching action assistant to Default, clear per-action model override
    if (field.endsWith('_assistant') && value === 'default') {
      const actionKey = field.replace('_assistant', '') as keyof ModelDefaults;
      if (actionKey in newDefaults) {
        (newDefaults as any)[actionKey] = '';
      }
    }

    setDefaults(newDefaults);
    await handleSave(newDefaults);
  };

  const modelsForAssistant = (assistant: string) => {
    return availableModels.filter((model) => {
      if (assistant === 'claude_cli') {
        return model.service === 'anthropic';
      }
      if (assistant === 'cursor') {
        return model.service === 'cursor';
      }
      return true;
    });
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-600 rounded-lg shadow p-6">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  const actions = [
    { key: 'plan', label: 'Plan', icon: '📋', description: 'Generate implementation plan for tickets' },
    { key: 'implement', label: 'Implement', icon: '💻', description: 'Write code implementation' },
    { key: 'push', label: 'Push', icon: '🚀', description: 'Create PR and push changes' },
    { key: 'complic', label: 'Complic', icon: '🧠', description: 'General Complic assistant' }
  ];

  return (
    <div className="bg-white dark:bg-gray-600 rounded-lg shadow p-6">
      <div className="mb-6">
        <h2 className="text-xl font-sans font-bold text-gray-800 dark:text-white normal-case">Default Models</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 font-sans">
          Set a global Default assistant & model, then override per action when needed
        </p>
      </div>

      {availableModels.length === 0 ? (
        <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-400 rounded p-4">
          <p className="text-sm text-gray-800 dark:text-white font-sans">
            <strong>No models available.</strong> Please add API keys in the API Keys section first.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Global Default */}
          <div className="border border-blue-200 dark:border-blue-500 rounded-lg p-4 bg-blue-50/50 dark:bg-blue-900/20">
            <div className="flex items-start gap-3 mb-3">
              <span className="text-3xl">📌</span>
              <div>
                <h3 className="font-sans font-semibold text-gray-800 dark:text-white">
                  Default
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-300 font-sans">
                  Used when Plan / Implement / Push / Complic are set to Default
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-sans font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Assistant
                </label>
                <select
                  value={defaults.default_assistant}
                  onChange={(e) => handleFieldChange('default_assistant', e.target.value)}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-white font-sans text-sm disabled:opacity-50"
                >
                  {REAL_ASSISTANTS.map((assistant) => (
                    <option key={assistant.value} value={assistant.value}>
                      {assistant.label} - {assistant.description}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-sans font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Model
                </label>
                <select
                  value={defaults.default_model}
                  onChange={(e) => handleFieldChange('default_model', e.target.value)}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-white font-sans text-sm disabled:opacity-50"
                >
                  <option value="">Select a model…</option>
                  {modelsForAssistant(defaults.default_assistant).map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name} ({model.context}) - {model.service}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Action-specific overrides */}
          {actions.map((action) => {
            const assistantKey = `${action.key}_assistant` as keyof ModelDefaults;
            const selectedAssistant = defaults[assistantKey];
            const usesDefault = selectedAssistant === 'default';

            return (
              <div key={action.key} className="border border-gray-200 dark:border-gray-500 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <span className="text-3xl">{action.icon}</span>
                  <div className="flex-1 space-y-3">
                    <div>
                      <h3 className="font-sans font-semibold text-gray-800 dark:text-white mb-1">
                        {action.label}
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-gray-300 font-sans">
                        {action.description}
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-sans font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Assistant
                      </label>
                      <select
                        value={selectedAssistant}
                        onChange={(e) => handleFieldChange(assistantKey, e.target.value)}
                        disabled={saving}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-white font-sans text-sm disabled:opacity-50"
                      >
                        {ACTION_ASSISTANTS.map((assistant) => (
                          <option key={assistant.value} value={assistant.value}>
                            {assistant.label} - {assistant.description}
                          </option>
                        ))}
                      </select>
                    </div>

                    {usesDefault ? (
                      <p className="text-xs text-blue-700 dark:text-blue-300 font-sans">
                        Using Default: {REAL_ASSISTANTS.find(a => a.value === defaults.default_assistant)?.label || defaults.default_assistant}
                        {defaults.default_model ? ` · ${availableModels.find(m => m.id === defaults.default_model)?.name || defaults.default_model}` : ''}
                      </p>
                    ) : (
                      <div>
                        <label className="block text-xs font-sans font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Model (optional override)
                        </label>
                        <select
                          value={defaults[action.key as keyof ModelDefaults]}
                          onChange={(e) => handleFieldChange(action.key as keyof ModelDefaults, e.target.value)}
                          disabled={saving}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-white font-sans text-sm disabled:opacity-50"
                        >
                          <option value="">Use assistant&apos;s default model</option>
                          {modelsForAssistant(selectedAssistant).map((model) => (
                            <option key={model.id} value={model.id}>
                              {model.name} ({model.context}) - {model.service}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {saving && (
            <div className="flex items-center justify-center gap-2 pt-4 text-sm text-gray-600 dark:text-gray-300">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
              <span className="font-sans">Saving...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ModelDefaultsSettings;
